import {Directive, ElementRef, forwardRef, HostListener, Renderer2} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';


@Directive({
  selector: '[contenteditable]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ContentEditableDirective),
      multi: true,
    },
  ],
})
export class ContentEditableDirective implements ControlValueAccessor {
  private onChange: Function;
  private onTouched: Function;


  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
  ) {
  }


  @HostListener('input', ['$event'])
  input(event: InputEvent) {
    const value = this.elementRef.nativeElement.innerText;
    this.onChange(value);
  }


  @HostListener('blur')
  blur() {
    this.onTouched();
  }


  @HostListener('paste', ['$event'])
  paste(event: ClipboardEvent) {
    // Prevent the default paste behavior
    event.preventDefault();

    // Get the plain text from the clipboard
    let text = '';
    if (event.clipboardData) {
      text = event.clipboardData.getData('text/plain');
    } else if ((window as any).clipboardData) {
      // For Internet Explorer
      text = (window as any).clipboardData.getData('Text');
    }

    // Escape HTML characters
    text = text.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Insert the escaped text content into the contenteditable div at the selection
    if (document.queryCommandSupported('insertHTML')) {
      document.execCommand('insertHTML', false, text);
    } else { // Fallback for browsers that don't support 'insertHTML'
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        range.insertNode(document.createTextNode(text));
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  }


  writeValue(value: any): void {
    const normalizedValue = value == null ? '' : value;
    this.renderer.setProperty(this.elementRef.nativeElement, 'innerText', normalizedValue);
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }


  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }


  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'contentEditable', !isDisabled);
  }
}
